import { readJson, testLocalStorage, writeJson } from './storage.js'

export const MESSAGE_BUFFER_STORAGE_LIMIT = 100

export function messageBufferStorageKey(chatId) {
  if (chatId == null || String(chatId).trim() === '') return null
  return `messages-${String(chatId).trim()}`
}

const CHANNEL_NAME = 'ometrics-widget-message-buffer'

/** @type {BroadcastChannel | null} */
let channel = null

function getChannel() {
  if (typeof BroadcastChannel === 'undefined') return null
  if (!channel) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME)
    } catch {
      channel = null
    }
  }
  return channel
}

function trimMessages(messages) {
  if (!Array.isArray(messages)) return []
  return messages.slice(-MESSAGE_BUFFER_STORAGE_LIMIT)
}

/**
 * Persist the thread and notify other widget mounts (overlay, slide-out,
 * injected) on the same page. The native `storage` event does not fire in
 * the document that wrote the key, so we broadcast explicitly.
 */
export function publishMessageBuffer(chatId, messages, options = {}) {
  const key = messageBufferStorageKey(chatId)
  if (!key) return

  const trimmed = trimMessages(messages)
  if (testLocalStorage()) {
    writeJson(key, trimmed)
  }

  const detail = {
    chatId: String(chatId).trim(),
    messages: trimmed,
    sourceInstanceId: options.sourceInstanceId ?? null,
    ts: Date.now(),
  }

  const bc = getChannel()
  if (bc) {
    try {
      bc.postMessage(detail)
    } catch {
      // ignore closed channel / quota
    }
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('ometrics:message-buffer', { detail }),
    )
  }
}

export function loadMessageBuffer(chatId) {
  const key = messageBufferStorageKey(chatId)
  if (!key) return []
  const stored = readJson(key, [])
  return Array.isArray(stored) ? stored : []
}

function shouldApplyRemoteUpdate(current, incoming) {
  if (!Array.isArray(incoming)) return false
  if (!Array.isArray(current) || current.length !== incoming.length) {
    return true
  }
  const lastCur = current[current.length - 1]
  const lastIn = incoming[incoming.length - 1]
  if (!lastCur || !lastIn) return true
  if (lastCur.id !== lastIn.id) return true
  if (lastCur.text !== lastIn.text) return true
  if (Boolean(lastCur.streaming) !== Boolean(lastIn.streaming)) return true
  if (
    Array.isArray(lastCur.suggestions) !== Array.isArray(lastIn.suggestions) ||
    (lastCur.suggestions?.length ?? 0) !== (lastIn.suggestions?.length ?? 0)
  ) {
    return true
  }
  if (
    Array.isArray(lastCur.buttons) !== Array.isArray(lastIn.buttons) ||
    (lastCur.buttons?.length ?? 0) !== (lastIn.buttons?.length ?? 0)
  ) {
    return true
  }
  return false
}

/**
 * Subscribe to message-buffer updates for an agent (`chatId`). Returns an
 * unsubscribe function. Ignores events published by `ignoreSourceInstanceId`.
 */
export function subscribeMessageBuffer(
  chatId,
  onMessages,
  { ignoreSourceInstanceId = null } = {},
) {
  const normalizedChatId =
    chatId != null ? String(chatId).trim() : ''
  if (!normalizedChatId || typeof onMessages !== 'function') {
    return () => {}
  }

  const apply = (detail) => {
    if (!detail || detail.chatId !== normalizedChatId) return
    if (
      ignoreSourceInstanceId &&
      detail.sourceInstanceId === ignoreSourceInstanceId
    ) {
      return
    }
    onMessages(detail.messages, detail)
  }

  const onCustom = (event) => {
    apply(event?.detail)
  }

  const bc = getChannel()
  const onBroadcast = (event) => {
    apply(event?.data)
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('ometrics:message-buffer', onCustom)
  }
  if (bc) {
    bc.addEventListener('message', onBroadcast)
  }

  return () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('ometrics:message-buffer', onCustom)
    }
    if (bc) {
      bc.removeEventListener('message', onBroadcast)
    }
  }
}

/**
 * Merge remote messages into local React state only when the payload changed.
 */
export function mergeRemoteMessageBuffer(current, incoming) {
  if (!shouldApplyRemoteUpdate(current, incoming)) {
    return current
  }
  return trimMessages(incoming)
}
